/**
 * Batch-load property rows for AddedPropertyReportsWatcher polling.
 *
 * @module store/addedPropertyReportsWatcher/fetchWatcherPropertiesBatch
 */

import { graphqlRequest } from "@/graphql/client";
import { batchGetProperties } from "@/graphql/queries";
import { extractBodyString, isSuccessStatusCode, normalizeProperty } from "@/store/propertyCore/propertyApi";
import { cachedPropertyIdMatchesKey } from "@/store/propertyCore/propertyCacheUtils";

/**
 * @param {string|null|undefined} propertyId
 * @returns {string}
 */
export function toPropertyLookupId(propertyId) {
  const normalizedId = String(propertyId ?? "").trim();
  if (!normalizedId) return "";
  return normalizedId.replace(/^property#/i, "").trim();
}

/**
 * @param {Record<string, unknown>} propertiesMap
 * @param {string} requestedPropertyId
 * @returns {object|null}
 */
function pickPropertyFromMap(propertiesMap, requestedPropertyId) {
  const requested = String(requestedPropertyId ?? "").trim();
  if (!requested || !propertiesMap || typeof propertiesMap !== "object") return null;

  const lookupId = toPropertyLookupId(requested);
  if (propertiesMap[requested]) return /** @type {object} */ (propertiesMap[requested]);
  if (lookupId && propertiesMap[lookupId]) return /** @type {object} */ (propertiesMap[lookupId]);

  const entries = Object.values(propertiesMap);
  const match =
    entries.find((item) => item && cachedPropertyIdMatchesKey(/** @type {object} */ (item).id, requested)) || null;
  return /** @type {object|null} */ (match);
}

/**
 * @param {string} customer
 * @param {string[]} propertyIds
 * @returns {Promise<Map<string, object>>} Map requestedPropertyId → normalized property
 */
export async function fetchWatcherPropertiesBatch(customer, propertyIds) {
  const out = new Map();
  const customerId = String(customer ?? "").trim();
  if (!customerId || !Array.isArray(propertyIds) || propertyIds.length === 0) return out;

  const requestedList = [...new Set(propertyIds.map((id) => String(id ?? "").trim()).filter(Boolean))];
  const lookupIds = [...new Set(requestedList.map(toPropertyLookupId).filter(Boolean))];
  if (lookupIds.length === 0) return out;

  let batchResponse;
  try {
    batchResponse = await graphqlRequest({
      query: batchGetProperties,
      variables: {
        customer_id: customerId,
        property_ids: lookupIds,
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });
  } catch (err) {
    console.log("[batchGetProperties]", err?.message ?? err);
    return out;
  }

  const batchResult = batchResponse?.data?.batchGetProperties;
  if (!isSuccessStatusCode(batchResult?.statusCode) || batchResult?.body == null) return out;

  let parsed;
  try {
    const bodyString = extractBodyString(batchResult.body);
    parsed = JSON.parse(bodyString || "{}");
  } catch {
    return out;
  }

  const propertiesMap = parsed?.properties;
  if (!propertiesMap || typeof propertiesMap !== "object") return out;

  for (const requestedPropertyId of requestedList) {
    const raw = pickPropertyFromMap(propertiesMap, requestedPropertyId);
    if (!raw || typeof raw !== "object") continue;
    try {
      out.set(requestedPropertyId, normalizeProperty(raw, 0));
    } catch {
      out.set(requestedPropertyId, raw);
    }
  }

  return out;
}
